import { useEffect, useMemo, useState } from 'react';

import { Box } from '@zillow/constellation-10';

import { overlayStyles, spotlightStyles } from '../styles';

import { TourPopover } from './TourPopover';

import type React from 'react';
import type { FeatureTourStep, Rect, RectOffset } from '..';

type Props = {
  step: FeatureTourStep;
  index: number;
  total: number;
  targetEl: HTMLElement;
  spotlightPadding: number;
  allowScroll?: boolean;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onSkip: () => void;
};

const getRect = (el: HTMLElement, offset?: RectOffset): Rect => {
  const r = el.getBoundingClientRect();
  return {
    top: r.top + (offset?.top ?? 0),
    left: r.left + (offset?.left ?? 0),
    width: r.width + (offset?.width ?? 0),
    height: r.height + (offset?.height ?? 0),
  };
};

export function TourOverlay({
  step,
  index,
  total,
  targetEl,
  spotlightPadding,
  allowScroll = false,
  onNext,
  onPrev,
  onClose,
  onSkip,
}: Props) {
  const [rect, setRect] = useState<Rect>(() => getRect(targetEl, step.targetRectOffset));

  useEffect(() => {
    const update = () => setRect(getRect(targetEl, step.targetRectOffset));
    update();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [targetEl, step.id, step.targetRectOffset]);

  // Trap ESC to close
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, onNext, onPrev]);

  // Prevent scrolling while tour is open (unless allowScroll is true)
  useEffect(() => {
    if (allowScroll) {
      return;
    }

    const html = document.documentElement;
    const originalHtmlOverflow = html.style.overflow;

    html.style.setProperty('overflow', 'hidden', 'important');

    return () => {
      html.style.overflow = originalHtmlOverflow;
    };
  }, [allowScroll]);

  // Spotlight position is viewport-relative (parent is position: fixed)
  // getBoundingClientRect() already returns viewport-relative coordinates
  // applies the dynamic positioning (where & how big)
  const spotlightStyle = useMemo(() => {
    return {
      top: Math.max(0, rect.top - spotlightPadding),
      left: Math.max(0, rect.left - spotlightPadding),
      width: rect.width + spotlightPadding * 2,
      height: rect.height + spotlightPadding * 2,
    } as React.CSSProperties;
  }, [rect, spotlightPadding]);

  return (
    <Box css={overlayStyles} role="dialog" aria-modal="true" aria-label="Feature Tour">
      {/* Spotlight creates the dark overlay with a cutout via box-shadow */}
      <Box css={spotlightStyles} style={spotlightStyle} />

      {/* Popover */}
      <TourPopover
        step={step}
        index={index}
        total={total}
        targetRect={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
        onNext={onNext}
        onPrev={onPrev}
        onClose={onClose}
        onSkip={onSkip}
      />
    </Box>
  );
}
